import React, { useState } from "react";
import PropTypes from "prop-types";
import { Row, Col, Label, Input, FormGroup, FormFeedback } from "reactstrap";
import ClientAdminPaymentWarning from "./client-admin-payment-warning";

const propTypes = {
  cardInfo: PropTypes.arrayOf(
    PropTypes.shape({
      credit_card_number_last_4: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      credit_card_type: PropTypes.string,
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      last_used: PropTypes.node,
      pretty_expiration: PropTypes.node,
      pretty_info: PropTypes.node
    })
  ),
  images: PropTypes.shape({
    cards: PropTypes.string,
    cards2x: PropTypes.string
  }).isRequired,
  errorData: PropTypes.shape({
    base: PropTypes.node,
    billing_zip: PropTypes.node,
    credit_card_cvv: PropTypes.node,
    credit_card_expiration_month: PropTypes.node,
    credit_card_expiration_year: PropTypes.node,
    credit_card_number: PropTypes.node
  }),
  hideExistingCards: PropTypes.bool,
  handleChange: PropTypes.func.isRequired,
  accountholderName: PropTypes.string.isRequired,
  showClientAdminPaymentWarning: PropTypes.bool.isRequired
};

const CreditCardFields = ({
  cardInfo = [],
  images,
  errorData = {},
  hideExistingCards = false,
  handleChange,
  accountholderName,
  showClientAdminPaymentWarning
}) => {
  const [selectedCard, setSelectedCard] = useState(
    cardInfo && cardInfo.length > 0 && !hideExistingCards ? cardInfo[0] : null
  );

  const changeCard = event => {
    event.preventDefault();
    const selectedId = parseInt(event.target.value, 10);

    setSelectedCard(selectedId < 0 ? null : cardInfo.find(cc => cc.id === selectedId));
    handleChange(event, "transactionId");
  };

  const displayError = key => {
    return (
      errorData && Object.keys(errorData).length > 0 && errorData[key] && errorData[key].length > 0
    );
  };

  const currentYear = new Date().getFullYear();
  const yearOptions = Array(12)
    .fill()
    .map((_, idx) => currentYear + idx);
  const monthOptions = Array(12)
    .fill()
    .map((_, idx) => 1 + idx);

  return (
    <div className="purchase-modal__payment-information">
      {showClientAdminPaymentWarning && (
        <ClientAdminPaymentWarning accountholderName={accountholderName} />
      )}
      {cardInfo && cardInfo.length > 0 && !hideExistingCards && (
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div className="font-weight-bold mr-3">Card Details</div>
          <FormGroup className="mb-0">
            <Input type="select" onChange={event => changeCard(event)} id="card-select">
              {cardInfo.map(card => (
                <option key={card.id} value={card.id}>
                  {card.pretty_info}
                </option>
              ))}
              <option value={-1}>Add a new card</option>
            </Input>
          </FormGroup>
        </div>
      )}
      {selectedCard ? (
        <div>
          <Row>
            <Col>
              <img
                src={images[selectedCard.credit_card_type]}
                alt={selectedCard.credit_card_type}
              />
              , **** **** **** {selectedCard.credit_card_number_last_4}
              <br />
              Expires {selectedCard.pretty_expiration}
            </Col>
          </Row>
          <Row>
            <Col className="text-muted font-italic font-size-small">
              Last Used {selectedCard.last_used}
            </Col>
          </Row>
        </div>
      ) : (
        <div>
          <FormGroup
            color={displayError("credit_card_number") ? "danger" : undefined}
            className="mb-2"
          >
            <Row className="mb-1">
              <Col className="d-flex justify-content-between align-items-center ">
                <Label className="mb-0 text-secondary" for="credit_card_number">
                  Credit card
                </Label>
                <img
                  className="mb-1"
                  data-at2x={images.cards2x}
                  src={images.cards}
                  alt="card options"
                />
              </Col>
            </Row>
            <Row>
              <Col>
                <Input
                  id="credit_card_number"
                  onChange={event => handleChange(event, "creditCardNumber")}
                  state={displayError("credit_card_number") ? "danger" : undefined}
                />
              </Col>
            </Row>
            {displayError("credit_card_number") && (
              <FormFeedback>{errorData.credit_card_number}</FormFeedback>
            )}
          </FormGroup>
          <Row className="mb-2">
            <Col>
              <FormGroup
                color={
                  displayError("credit_card_expiration_month") ||
                  displayError("credit_card_expiration_year")
                    ? "danger"
                    : undefined
                }
                className="mb-0"
              >
                <Row>
                  <Col className="text-secondary px-0">
                    <Label htmlFor="expiring_month">Expiration date</Label>
                  </Col>
                </Row>
                <Row>
                  <Col className="pr-1 pl-0">
                    <Input
                      type="select"
                      id="expiring_month"
                      state={displayError("credit_card_expiration_month") ? "danger" : undefined}
                      onChange={event => handleChange(event, "expirationMonth")}
                    >
                      {monthOptions.map(month => (
                        <option key={month} value={month}>
                          {month}
                        </option>
                      ))}
                    </Input>
                  </Col>
                  <Col className="pl-1">
                    <Input
                      type="select"
                      id="expiring_year"
                      state={displayError("credit_card_expiration_year") ? "danger" : undefined}
                      onChange={event => handleChange(event, "expirationYear")}
                    >
                      {yearOptions.map(year => (
                        <option key={year} value={year}>
                          {year}
                        </option>
                      ))}
                    </Input>
                  </Col>
                </Row>
                {displayError("credit_card_expiration_month") && (
                  <FormFeedback>{errorData.credit_card_expiration_month}</FormFeedback>
                )}
                {displayError("credit_card_expiration_year") && (
                  <FormFeedback>{errorData.credit_card_expiration_year}</FormFeedback>
                )}
              </FormGroup>
            </Col>
            <Col>
              <FormGroup
                color={displayError("credit_card_cvv") ? "danger" : undefined}
                className="mb-0"
              >
                <Row>
                  <Col className="text-secondary">
                    <Label for="cvv">Security code</Label>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <Input
                      maxLength="4"
                      id="cvv"
                      state={displayError("credit_card_cvv") ? "danger" : undefined}
                      onChange={event => handleChange(event, "cvv")}
                    />
                  </Col>
                </Row>
                {displayError("credit_card_cvv") && (
                  <FormFeedback>{errorData.credit_card_cvv}</FormFeedback>
                )}
              </FormGroup>
            </Col>
          </Row>
          <FormGroup color={displayError("billing_zip") ? "danger" : undefined}>
            <Row className="mb-0">
              <Col className="text-secondary">
                <Label className="text-secondary" for="zip_code">
                  Billing zip code
                </Label>
              </Col>
            </Row>
            <Row>
              <Col>
                <Input
                  id="zip_code"
                  state={displayError("billing_zip") ? "danger" : undefined}
                  onChange={event => handleChange(event, "zipCode")}
                />
              </Col>
            </Row>
            {displayError("billing_zip") && <FormFeedback>{errorData.billing_zip}</FormFeedback>}
          </FormGroup>
        </div>
      )}
    </div>
  );
};

CreditCardFields.propTypes = propTypes;

export default CreditCardFields;
